import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { db } from '../../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { getSellerListings, type Listing } from '../../services/listings';
import { getBuyerOrders } from '../../services/orders';
import { auth } from '../../lib/firebase';
import { recordSellerProfileView } from '../../services/stats';
import { getAverageRatingForSeller, getSellerReviews } from '../../services/reviews';
import { getOrdersCountForSeller, getCompletedOrdersCountForSeller } from '../../services/orders';
import { formatDate } from '../../utils/date';
import { createConversation } from '../../services/messaging';

interface SellerProfile {
  uid: string;
  name: string;
  university?: string;
  photoURL?: string;
  isVerified?: boolean;
  plan?: 'basic' | 'pro';
  bio?: string;
  rating?: number;
  totalSales?: number;
  joinedDate?: string;
}

const SellerProfilePage: React.FC = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState<SellerProfile | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [ordersCount, setOrdersCount] = useState<number>(0);
  const [completedOrders, setCompletedOrders] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<number>(0);
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  function buildProfileShareUrl() {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const params = new URLSearchParams({
      id: seller?.uid || ''
    });
    return `${origin}/api/profile-share?${params.toString()}`;
  }

  function getProfileShareMessage() {
    const name = seller?.name || 'A trusted seller';
    const uni = seller?.university ? ` at ${seller.university}` : '';
    const plan = seller?.plan === 'pro' ? 'Pro Seller' : 'Seller';
    const verified = seller?.isVerified ? ' • Verified' : '';
    return `Check out ${name}${uni} on Campus Market — ${plan}${verified}`;
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!sellerId) return;

        const sellerRef = doc(db, 'users', sellerId);
        const snap = await getDoc(sellerRef);
        if (!snap.exists()) {
          setLoading(false);
          return;
        }
        const data = snap.data() as any;
        // Public display name = store/vendor name (username); real name stays for the owner.
        setSeller({ uid: sellerId, ...data, name: (data as any).username || (data as any).fullName || (data as any).name || 'Seller', plan: (data as any).plan || 'basic' });

        // Listings by this seller
        const sellerListings = await getSellerListings(sellerId);
        setListings(sellerListings || []);

        // Orders count for this seller
        const [ordersTotal, completedTotal] = await Promise.all([
          getOrdersCountForSeller(sellerId),
          getCompletedOrdersCountForSeller(sellerId),
        ]);
        setOrdersCount(ordersTotal);
        setCompletedOrders(completedTotal);
        
        // Completed orders count computed via service helpers above
        
        // Get seller rating from reviews (average of buyer ratings)
        const { average } = await getAverageRatingForSeller(sellerId);
        setRating(parseFloat(average.toFixed(1)));
      } catch (err) {
        console.error('Error loading seller profile:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [sellerId]);

  // Count seller profile views for logged-in viewers (excluding the seller themselves)
  useEffect(() => {
    const viewer = auth.currentUser;
    if (!viewer || !sellerId) return;
    if (viewer.uid === sellerId) return; // don't count self-views
    (async () => {
      try {
        await recordSellerProfileView(sellerId);
      } catch (e) {
        console.warn('Failed to record profile view', e);
      }
    })();
  }, [sellerId, seller]);

  const formatBadge = () => {
    if (seller?.plan === 'pro') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading seller profile...</p>
        </div>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Seller not found</h1>
          <p className="text-gray-600 mb-6">The seller profile could not be located.</p>
          <button onClick={() => navigate(-1)} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">Go Back</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8 border border-gray-200 text-gray-900">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
                {seller.photoURL ? (
                  <img src={seller.photoURL} alt={seller.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <i className="ri-user-line text-5xl"></i>
                  </div>
                )}
              </div>
              {(seller.isVerified) && (
                <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1 border-2 border-white">
                  <i className="ri-shield-check-line text-sm"></i>
                </div>
              )}
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold">{seller.name}</h1>
                  {auth.currentUser?.uid === seller.uid && (seller as any).fullName && (seller as any).fullName !== seller.name && (
                    <p className="mt-1 text-sm text-white/70">
                      <i className="ri-lock-line mr-1"></i>Your full name: {(seller as any).fullName} <span className="opacity-70">(only you can see this)</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${seller.plan === 'pro' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}`}>
                    <i className={`ri-${seller.plan === 'pro' ? 'crown' : 'user'}-line mr-1`}></i>
                    {seller.plan === 'pro' ? 'Pro Seller' : 'Basic Seller'}
                  </span>
                  {(seller.isVerified) && (
                    <span className="inline-flex items-center px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full">
                      <i className="ri-shield-check-line mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>
              </div>
              
              {seller.university && (
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <i className="ri-school-line"></i>
                  <p className="text-lg">{seller.university}</p>
                </div>
              )}
              
              {seller.bio && (
                <p className="text-gray-700 mb-6 max-w-2xl">{seller.bio}</p>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{listings.length}</div>
                  <div className="text-sm text-gray-600">Active Listings</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{completedOrders}</div>
                  <div className="text-sm text-gray-600">Completed Orders</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{rating}★</div>
                  <div className="text-sm text-gray-600">Seller Rating</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <button 
                  onClick={async () => {
                    if (!auth.currentUser) {
                      navigate(`/auth/sign-in?redirect=/seller/${seller?.uid}`);
                      return;
                    }
                    try {
                      const convoId = await createConversation(
                        seller!.uid,
                        seller!.name || 'Seller',
                        (seller as any).email || ''
                      );
                      navigate(`/messages?conversation=${convoId}`);
                    } catch (e) {
                      console.error('Failed to start conversation:', e);
                      navigate(`/messages`);
                    }
                  }}
                  className="bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-black transition-all duration-200 shadow-sm flex items-center gap-2"
                >
                  <i className="ri-message-2-line"></i>
                  Message Seller
                </button>

                <button
                  onClick={() => setShowShareModal(true)}
                  className="bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-all duration-200 shadow-sm flex items-center gap-2"
                >
                  <i className="ri-share-line"></i>
                  Share Profile
                </button>

              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Products by {seller.name}</h2>
              <p className="text-gray-600 mt-1">Browse all items from this seller</p>
            </div>
            <div className="text-lg font-semibold text-gray-900">{listings.length} items</div>
          </div>

          {listings.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <i className="ri-box-2-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No products yet</h3>
              <p className="text-gray-600">This seller hasn't listed any products yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/product/${item.id}`} 
                  className="group border border-gray-100 rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="relative overflow-hidden">
                    {(item.images && item.images[0]) ? (
                      <img 
                        src={item.images[0]} 
                        alt={item.title} 
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-gray-400"></i>
                      </div>
                    )}
                    {seller.plan === 'pro' && (
                      <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <i className="ri-verified-badge-line"></i>
                        Verified
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-gray-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-2xl font-bold text-gray-900 mt-2">
                      ₦{(item.price as number)?.toLocaleString?.() || item.price}
                    </p>
                    
                    <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
                      <span className="capitalize bg-gray-100 px-2 py-1 rounded">{item.condition || 'Good'}</span>
                      <div className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        <span>{item.views || 0}</span>
                      </div>
                    </div>
                    
                    {(() => {
                      const listed = formatDate(item.createdAt);
                      return listed ? (
                        <div className="mt-2 text-xs text-gray-500">Listed {listed}</div>
                      ) : null;
                    })()}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Reviews about this seller */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Recent Reviews</h2>
              <p className="text-gray-600 mt-1">What buyers say about {seller?.name}</p>
            </div>
          </div>
          <SellerReviewsSection sellerId={seller?.uid || ''} />
        </div>

        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Share Seller Profile</h3>
                <button onClick={() => { setShowShareModal(false); setCopiedShare(false); }} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-md overflow-hidden bg-gray-100">
                    {seller?.photoURL ? (
                      <img src={seller.photoURL} alt={seller.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <i className="ri-user-line"></i>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{seller?.name}</p>
                    {seller?.university && <p className="text-sm text-gray-600">{seller.university}</p>}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                  {getProfileShareMessage()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Share Link</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={buildProfileShareUrl()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(buildProfileShareUrl());
                          setCopiedShare(true);
                          setTimeout(() => setCopiedShare(false), 2000);
                        } catch (e) {
                          alert('Could not copy link');
                        }
                      }}
                      className="px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                    >
                      {copiedShare ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">This link includes Open Graph tags so WhatsApp/Twitter can render a rich preview.</p>
                </div>

                <div className="flex justify-end">
                  <button onClick={() => { setShowShareModal(false); setCopiedShare(false); }} className="px-4 py-2 bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200">Done</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support and Reporting */}
        <div className="mt-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={`/support`}
              className="bg-gray-100 text-gray-900 px-6 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-sm flex items-center gap-2"
            >
              <i className="ri-lifebuoy-line"></i>
              Contact Support
            </Link>

            <Link
              to={`/report?type=seller&id=${seller.uid}&seller=${encodeURIComponent(seller.name)}`}
              className="bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-all duration-200 shadow-sm flex items-center gap-2"
            >
              <i className="ri-flag-2-line"></i>
              Report Seller
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const SellerReviewsSection: React.FC<{ sellerId: string }> = ({ sellerId }) => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState<boolean>(false);

  useEffect(() => {
    if (!sellerId) return;
    (async () => {
      try {
        setLoadingReviews(true);
        const items = await getSellerReviews(sellerId);
        setReviews(items);
      } catch (e) {
        console.warn('Failed to load seller reviews', e);
      } finally {
        setLoadingReviews(false);
      }
    })();
  }, [sellerId]);

  if (loadingReviews) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading reviews...</p>
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return <p className="text-gray-600">No reviews yet.</p>;
  }

  return (
    <div className="space-y-4">
      {reviews.slice(0, 6).map((rev) => (
        <div key={rev.id} className="border border-gray-100 rounded-md p-4">
          <div className="flex items-center justify-between">
            <p className="font-medium text-gray-900">{rev.reviewerName}</p>
            <div className="flex items-center">
              {[1,2,3,4,5].map(star => (
                <i key={star} className={`ri-star-fill ${star <= (rev.rating || 0) ? 'text-yellow-400' : 'text-gray-300'} mr-1`}></i>
              ))}
            </div>
          </div>
          <p className="text-gray-700 mt-2">{rev.comment}</p>
          <p className="text-xs text-gray-500 mt-1">{rev.createdAt?.toDate ? rev.createdAt.toDate().toLocaleString() : ''}</p>
        </div>
      ))}
    </div>
  );
};

export default SellerProfilePage;